/*
 * Milano promotion display
 *
 * This is presentation-only. Discount Ninja calculates every amount; this
 * controller only renders values from its public product/cart events.
 */
(function () {
  'use strict';

  if (window.__milanoPromotionDisplayBound) return;
  window.__milanoPromotionDisplayBound = true;

  var config = window.MILANO_PROMOTION_DISPLAY_V2;
  if (!config || !config.enabled) return;

  var state = {
    cartLinesByKey: Object.create(null),
    lastProductEvent: null,
    lastPdpSignature: '',
    productObserver: null,
    productTimer: null,
    selectedSellingPlanId: ''
  };

  function getEventData(event) {
    var detail = event && event.detail;
    var data = detail && detail.data;
    return Array.isArray(data) ? data[0] : data;
  }

  function field(object, names) {
    if (!object || typeof object !== 'object') return undefined;

    var keys = Object.keys(object);
    for (var i = 0; i < names.length; i++) {
      var expected = names[i].toLowerCase();
      for (var j = 0; j < keys.length; j++) {
        if (keys[j].toLowerCase() === expected) return object[keys[j]];
      }
    }

    return undefined;
  }

  function number(value) {
    if (value === null || value === undefined || value === '') return null;
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function cents(value) {
    var parsed = number(value);
    if (parsed === null || parsed < 0) return null;
    return Math.round(parsed);
  }

  function truthy(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  function normalized(value) {
    return String(value || '').trim().toLowerCase();
  }

  function expectedTokens() {
    return [config.offerToken, config.triggerToken]
      .map(normalized)
      .filter(function (token) { return token.length > 0; });
  }

  function hasExpectedToken(record) {
    var expected = expectedTokens();
    if (!expected.length || !record) return false;

    var actual = [record.offerToken, record.triggerToken, record.promotionToken]
      .map(normalized)
      .filter(function (token) { return token.length > 0; });

    return actual.some(function (token) { return expected.indexOf(token) !== -1; });
  }

  function percentMatches(originalCents, discountedCents, reportedPercentage) {
    var percentage = number(reportedPercentage);
    if (percentage === null && originalCents > discountedCents) {
      percentage = ((originalCents - discountedCents) / originalCents) * 100;
    }

    return percentage !== null && Math.abs(percentage - Number(config.percentage)) <= 0.25;
  }

  function formatMoney(value) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(value, config.moneyFormat || window.theme.moneyFormat);
    }

    return '$' + (value / 100).toFixed(2);
  }

  function dispatch(name) {
    try {
      document.dispatchEvent(new CustomEvent(name));
    } catch (error) {
      // Price decoration must never affect the storefront if CustomEvent fails.
    }
  }

  function productRoot() {
    return document.querySelector('[data-promotion-display-surface="pdp"][data-promotion-eligible="true"]');
  }

  function readMoneyCents(element) {
    if (!element) return null;

    var attributes = [
      'data-limoniapps-discountninja-price-cents-storecurrency',
      'data-limoniapps-discountninja-price-cents-basecurrency'
    ];
    var candidates = [element];
    var selector = '[' + attributes.join('],[') + ']';
    var descendants = element.querySelectorAll(selector);

    for (var i = 0; i < descendants.length; i++) candidates.push(descendants[i]);

    for (var candidateIndex = 0; candidateIndex < candidates.length; candidateIndex++) {
      for (var attributeIndex = 0; attributeIndex < attributes.length; attributeIndex++) {
        var value = candidates[candidateIndex].getAttribute && candidates[candidateIndex].getAttribute(attributes[attributeIndex]);
        var parsed = cents(value);
        if (parsed !== null) return parsed;
      }
    }

    return null;
  }

  function readPdpDomResult(root) {
    if (!root) return { rendered: false, originalCents: null, discountedCents: null };

    var original = root.querySelector('.limoniapps-discountninja-strikethrough');
    var discounted = root.querySelector('.limoniapps-discountninja-shortcode-discounted_price');
    if (!original || !discounted) return { rendered: false, originalCents: null, discountedCents: null };

    return {
      rendered: true,
      originalCents: readMoneyCents(original),
      discountedCents: readMoneyCents(discounted)
    };
  }

  function normalizeProductEvent(payload) {
    if (!payload || typeof payload !== 'object') return null;

    return {
      originalCents: cents(field(payload, ['originalPrice'])),
      discountedCents: cents(field(payload, ['discountedPrice'])),
      percentage: number(field(payload, ['percentage'])),
      variantId: String(field(payload, ['variantId']) || ''),
      productId: String(field(payload, ['productId']) || ''),
      productHandle: normalized(field(payload, ['productHandle'])),
      offerToken: field(payload, ['offerToken']),
      triggerToken: field(payload, ['triggerToken']),
      promotionToken: field(payload, ['promotionToken']),
      discountCode: field(payload, ['discountCode']),
      discountedByOffer: field(payload, ['isDiscountedByOffer']),
      sellingPlanId: String(field(payload, ['sellingPlan', 'sellingPlanId']) || '')
    };
  }

  function productEventTargetsRoot(record, root) {
    if (!record || !root) return false;
    if (record.variantId && record.variantId !== String(root.getAttribute('data-promotion-variant-id') || '')) return false;
    if (record.productId && record.productId !== String(root.getAttribute('data-promotion-product-id') || '')) return false;
    if (record.productHandle && record.productHandle !== normalized(root.getAttribute('data-promotion-product-handle'))) return false;
    return Boolean(record.variantId || record.productId || record.productHandle);
  }

  function productEventMatchesRoot(record, root) {
    if (!productEventTargetsRoot(record, root)) return false;
    if (record.discountedByOffer === false || record.discountedByOffer === 'false') return false;
    if (record.sellingPlanId) return false;
    return hasExpectedToken(record);
  }

  function pdpHasSellingPlan(root) {
    if (state.selectedSellingPlanId) return true;
    if (!root || !root.closest) return false;

    var scope = root.closest('[data-section-type*="product"], product-info, [data-product-information]') || document;
    var inputs = scope.querySelectorAll('form[data-product-form] [name="selling_plan"], form[data-product-form] .selected-selling-plan-id');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].disabled && String(inputs[i].value || '').trim()) return true;
    }

    return false;
  }

  function pdpBasePriceMatches(root, originalCents) {
    var basePrice = cents(root && root.getAttribute('data-promotion-base-price'));
    return basePrice !== null && originalCents !== null && Math.abs(basePrice - originalCents) <= 1;
  }

  function currentServiceCents() {
    try {
      var value = typeof window.MILANO_getSelectedServiceCents === 'function'
        ? Number(window.MILANO_getSelectedServiceCents())
        : 0;
      return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
    } catch (error) {
      return 0;
    }
  }

  function ensurePdpLabel(root) {
    var label = document.querySelector('[data-promotion-display-pdp-label]');

    if (!label) {
      label = document.createElement('span');
      label.className = 'promotion-display__label promotion-display__title-label';
      label.setAttribute('data-promotion-display-label', '');
      label.setAttribute('data-promotion-display-pdp-label', '');
      label.setAttribute('role', 'status');
      label.setAttribute('aria-live', 'polite');
      label.hidden = true;
      var titleBlock = document.querySelector('.product__block--title');
      if (titleBlock) titleBlock.appendChild(label);
    }

    var variantTitle = document.querySelector('.product__block--title .product__title__variant');
    if (variantTitle && label.parentNode !== variantTitle) variantTitle.appendChild(label);
    return label;
  }

  function setPdpMerchandisingBadge(root, visible) {
    if (!root) return;

    var badges = root.querySelectorAll('[data-promotion-merchandising-badge]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].hidden = !visible;
    }
  }

  function normalizedPath(url) {
    if (!url) return '';

    try {
      var parsed = new URL(url, window.location.origin);
      var path = parsed.pathname.replace(/\/+$/, '');
      return path || '/';
    } catch (error) {
      return '';
    }
  }

  function enhancePromotionNavigation() {
    var label = String(config.featuredCollectionLabel || '').trim();
    var labels = document.querySelectorAll('[data-promotion-navigation-label]');
    for (var i = 0; i < labels.length; i++) {
      labels[i].textContent = label;
      labels[i].hidden = !label;
    }

    var navigationUrls = Array.isArray(config.navigationCollectionUrls)
      ? config.navigationCollectionUrls
      : [config.featuredCollectionUrl];
    var navigationPaths = navigationUrls.map(normalizedPath).filter(Boolean);
    var featuredPath = normalizedPath(config.featuredCollectionUrl);
    var links = document.querySelectorAll('a.navlink, a.mobile-navlink');

    function clearDecorations(link) {
      var badges = link.querySelectorAll('[data-promotion-navigation-label]');
      for (var badgeIndex = 0; badgeIndex < badges.length; badgeIndex++) badges[badgeIndex].remove();
      link.classList.remove('milano-promotion-nav-link', 'milano-promotion-nav-featured');
      var titleHosts = link.querySelectorAll('.milano-promotion-nav-title');
      for (var titleIndex = 0; titleIndex < titleHosts.length; titleIndex++) {
        titleHosts[titleIndex].classList.remove('milano-promotion-nav-title');
      }
    }

    if (!config.navigationHighlightEnabled || !label || (!navigationPaths.length && !featuredPath)) {
      for (var clearIndex = 0; clearIndex < links.length; clearIndex++) clearDecorations(links[clearIndex]);
      return;
    }

    for (var j = 0; j < links.length; j++) {
      var link = links[j];
      var linkPath = normalizedPath(link.getAttribute('href'));
      var isFeaturedCollection = Boolean(featuredPath && linkPath === featuredPath);
      var isConfiguredPromotionCollection = navigationPaths.indexOf(linkPath) !== -1;

      clearDecorations(link);
      if (!isFeaturedCollection && !isConfiguredPromotionCollection) continue;

      // The featured collection is the primary campaign destination. It gets
      // a full-row CTA treatment instead of the smaller secondary badge, even
      // when the menu places it at a different depth from the other sale links.
      if (isFeaturedCollection) {
        link.classList.add('milano-promotion-nav-featured');
        continue;
      }

      // Promotion labels are for collection discovery inside the menu flyout.
      // Do not decorate a first-level header link, even when its URL happens to
      // match one of the configured promotion collections.
      var isTopLevel = link.classList.contains('navlink--toplevel')
        || link.classList.contains('mobile-navlink--level-1')
        || link.hasAttribute('data-top-link');
      if (isTopLevel) continue;

      link.classList.add('milano-promotion-nav-link');

      var badge = document.createElement('span');
      badge.className = 'milano-promotion-nav-label';
      badge.setAttribute('data-promotion-navigation-label', '');
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = label;

      var badgeHost = link.querySelector('.navtext');
      if (!badgeHost && link.classList.contains('mobile-navlink')) {
        for (var childIndex = 0; childIndex < link.children.length; childIndex++) {
          var child = link.children[childIndex];
          if (child.tagName === 'SPAN' && !child.classList.contains('mobile-navlink__custom-badge')) {
            badgeHost = child;
            break;
          }
        }
      }

      if (badgeHost) {
        badgeHost.classList.add('milano-promotion-nav-title');
        badgeHost.appendChild(badge);
      } else {
        link.appendChild(badge);
      }
    }
  }

  function clearCartBar() {
    var bars = document.querySelectorAll('[data-promotion-display-surface="cart-bar"]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.getAttribute('data-promotion-confirmed') !== 'true') continue;

      var price = bar.querySelector('[data-product-price]');
      var basePrice = cents(bar.getAttribute('data-promotion-base-price'));
      if (price && basePrice !== null) price.textContent = formatMoney(basePrice);

      var dynamicCompare = bar.querySelector('[data-promotion-display-cart-bar-compare]');
      if (dynamicCompare) {
        dynamicCompare.textContent = '';
        dynamicCompare.hidden = true;
      }

      var nativeCompare = bar.querySelector('[data-promotion-display-native-cart-bar-compare]');
      if (nativeCompare) nativeCompare.hidden = false;

      var label = bar.querySelector('[data-promotion-display-label]');
      if (label) {
        label.textContent = '';
        label.hidden = true;
      }

      bar.removeAttribute('data-promotion-confirmed');
    }
  }

  function renderCartBar(result) {
    if (!config.surfaces || !config.surfaces.cartBar) return;

    var bars = document.querySelectorAll('[data-promotion-display-surface="cart-bar"][data-promotion-eligible="true"]');
    var serviceCents = currentServiceCents();
    var originalCents = result.originalCents + serviceCents;
    var discountedCents = result.discountedCents + serviceCents;

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var price = bar.querySelector('[data-product-price]');
      if (price) price.textContent = formatMoney(discountedCents);

      var nativeCompare = bar.querySelector('[data-promotion-display-native-cart-bar-compare]');
      if (nativeCompare) nativeCompare.hidden = true;

      var dynamicCompare = bar.querySelector('[data-promotion-display-cart-bar-compare]');
      if (dynamicCompare) {
        dynamicCompare.textContent = formatMoney(originalCents);
        dynamicCompare.hidden = false;
      }

      var label = bar.querySelector('[data-promotion-display-label]');
      if (label) {
        label.textContent = config.label;
        label.hidden = false;
      }

      bar.setAttribute('data-promotion-confirmed', 'true');
    }
  }

  function clearPdp() {
    var root = productRoot();
    if (root) {
      root.removeAttribute('data-promotion-confirmed');
      setPdpMerchandisingBadge(root, false);
    }

    var label = document.querySelector('[data-promotion-display-pdp-label]');
    if (label) {
      label.textContent = '';
      label.hidden = true;
    }

    clearCartBar();
    if (window.MILANO_PROMOTION_DISPLAY_PDP_RESULT) {
      window.MILANO_PROMOTION_DISPLAY_PDP_RESULT = null;
      state.lastPdpSignature = '';
      dispatch('milano:promotion-display:cleared');
    }
  }

  function reconcilePdp() {
    if (!config.surfaces || !config.surfaces.pdp) return clearPdp();

    var root = productRoot();
    if (!root) return clearPdp();
    if (!expectedTokens().length || pdpHasSellingPlan(root)) return clearPdp();

    var domResult = readPdpDomResult(root);
    if (!domResult.rendered) return clearPdp();

    var record = state.lastProductEvent;
    if (!productEventMatchesRoot(record, root)) return clearPdp();

    var originalCents = record.originalCents;
    var discountedCents = record.discountedCents;
    if (!(originalCents > discountedCents && discountedCents > 0)) return clearPdp();
    if (!pdpBasePriceMatches(root, originalCents)) return clearPdp();
    if (domResult.originalCents === null || Math.abs(domResult.originalCents - originalCents) > 1) return clearPdp();
    if (domResult.discountedCents === null || Math.abs(domResult.discountedCents - discountedCents) > 1) return clearPdp();

    var percentage = record.percentage !== null
      ? record.percentage
      : ((originalCents - discountedCents) / originalCents) * 100;
    if (!percentMatches(originalCents, discountedCents, percentage)) return clearPdp();

    var result = {
      originalCents: originalCents,
      discountedCents: discountedCents,
      discountCents: originalCents - discountedCents,
      ratio: discountedCents / originalCents,
      percentage: percentage,
      label: config.label,
      source: 'promotion-display-confirmed',
      variantId: String(root.getAttribute('data-promotion-variant-id') || '')
    };
    var signature = [result.variantId, result.originalCents, result.discountedCents, result.label].join('|');

    root.setAttribute('data-promotion-confirmed', 'true');
    setPdpMerchandisingBadge(root, true);
    if (config.pdpLabelEnabled === true) {
      var label = ensurePdpLabel(root);
      label.textContent = config.label;
      // Wait until the title-split script has created the color/subtitle span;
      // this prevents the label from briefly appearing on a separate line.
      label.hidden = !label.parentNode || !label.parentNode.classList.contains('product__title__variant');
    } else {
      // The approved launch treatment keeps the PDP campaign copy dormant.
      // Leave DN's confirmed price state intact, but never inject a second
      // label into the price row where it can race Splitit during hydration.
      var dormantLabels = document.querySelectorAll('[data-promotion-display-pdp-label]');
      for (var labelIndex = 0; labelIndex < dormantLabels.length; labelIndex++) {
        dormantLabels[labelIndex].textContent = '';
        dormantLabels[labelIndex].hidden = true;
      }
    }
    renderCartBar(result);

    window.MILANO_PROMOTION_DISPLAY_PDP_RESULT = result;
    if (state.lastPdpSignature !== signature) {
      state.lastPdpSignature = signature;
      dispatch('milano:promotion-display:confirmed');
    }
  }

  function schedulePdp(delay) {
    window.clearTimeout(state.productTimer);
    state.productTimer = window.setTimeout(reconcilePdp, typeof delay === 'number' ? delay : 60);
  }

  function observePdp() {
    if (state.productObserver) state.productObserver.disconnect();

    var root = productRoot();
    if (!root) return;

    state.productObserver = new MutationObserver(function () {
      schedulePdp(80);
    });
    state.productObserver.observe(root, { childList: true, subtree: true, characterData: true });

    // The color/subtitle span is created by the title-split bootstrap in a
    // sibling block. Observe that block too so the confirmed label moves onto
    // the same line as the color without waiting for another DN event.
    var titleBlock = document.querySelector('.product__block--title');
    if (titleBlock && titleBlock !== root) {
      state.productObserver.observe(titleBlock, { childList: true, subtree: true, characterData: true });
    }
  }

  function cartItemKey(item) {
    return String(field(item, ['key', 'lineKey']) || '');
  }

  function normalizeCartItem(item) {
    if (!item || typeof item !== 'object') return null;

    var price = field(item, ['price']) || {};
    var product = field(item, ['product']) || {};
    var line = field(item, ['line']) || {};
    var productPrice = field(price, ['product']) || {};
    var linePrice = field(price, ['line']) || {};
    var discount = field(item, ['discount']) || {};
    var productDiscount = field(discount, ['product']) || {};
    var lineDiscount = field(discount, ['line']) || {};
    var productOriginalCents = cents(field(productPrice, ['original']));
    var productDiscountedCents = cents(field(productPrice, ['discounted']));
    var lineOriginalCents = cents(field(linePrice, ['original']));
    var lineDiscountedCents = cents(field(linePrice, ['discounted']));
    var percentage = number(field(productDiscount, ['percentage']));
    if (percentage === null) percentage = number(field(lineDiscount, ['percentage']));

    if (productOriginalCents === null) productOriginalCents = cents(field(product, ['originalPrice']));
    if (productDiscountedCents === null) productDiscountedCents = cents(field(product, ['discountedPrice']));
    if (lineOriginalCents === null) lineOriginalCents = cents(field(line, ['originalPrice']));
    if (lineDiscountedCents === null) lineDiscountedCents = cents(field(line, ['discountedPrice']));
    if (percentage === null) percentage = number(field(product, ['percentage']));
    if (percentage === null) percentage = number(field(line, ['percentage']));

    var productDiscountCents = cents(field(productDiscount, ['amount']));
    var lineDiscountCents = cents(field(lineDiscount, ['amount']));
    if (productDiscountCents === null) productDiscountCents = cents(field(product, ['amount']));
    if (lineDiscountCents === null) lineDiscountCents = cents(field(line, ['amount']));

    var appliedPromotion = field(item, ['appliedPromotion']) || {};
    var sellingPlanAllocation = field(item, ['sellingPlanAllocation']);
    var sellingPlan = sellingPlanAllocation && field(sellingPlanAllocation, ['sellingPlan']);
    var sellingPlanId = field(product, ['sellingPlanId']);
    if (sellingPlanId === undefined || sellingPlanId === null || sellingPlanId === '') {
      sellingPlanId = field(item, ['sellingPlanId', 'selling_plan_id']);
    }
    if ((sellingPlanId === undefined || sellingPlanId === null || sellingPlanId === '') && sellingPlan) {
      sellingPlanId = field(sellingPlan, ['id']);
    }

    var quantity = number(field(item, ['quantity']));
    if (quantity === null) quantity = number(field(line, ['quantity']));

    return {
      key: cartItemKey(item),
      productOriginalCents: productOriginalCents,
      productDiscountedCents: productDiscountedCents,
      lineOriginalCents: lineOriginalCents,
      lineDiscountedCents: lineDiscountedCents,
      percentage: percentage,
      productDiscountCents: productDiscountCents,
      lineDiscountCents: lineDiscountCents,
      offerToken: field(appliedPromotion, ['offerToken']) || field(item, ['offerToken']),
      triggerToken: field(item, ['triggerToken']),
      promotionToken: field(appliedPromotion, ['promotionToken']) || field(item, ['promotionToken']),
      discountCode: field(appliedPromotion, ['discountCode']) || field(item, ['discountCode']),
      isGift: truthy(field(item, ['isGift'])),
      isSubscription: truthy(field(item, ['isSubscriptionProduct'])) ||
        truthy(field(product, ['hasSellingPlan'])) ||
        Boolean(sellingPlanAllocation) ||
        Boolean(String(sellingPlanId || '').trim()),
      quantity: quantity
    };
  }

  function isConfirmedCartItem(item) {
    if (!item || !item.key || item.isGift || item.isSubscription) return false;
    if (!(item.productOriginalCents > item.productDiscountedCents && item.productDiscountedCents > 0)) return false;
    if (!(item.lineOriginalCents > item.lineDiscountedCents && item.lineDiscountedCents > 0)) return false;
    if (!hasExpectedToken(item)) return false;
    if (!(item.productDiscountCents > 0 || item.lineDiscountCents > 0)) return false;
    return percentMatches(item.productOriginalCents, item.productDiscountedCents, item.percentage);
  }

  function empty(element) {
    while (element.firstChild) element.removeChild(element.firstChild);
  }

  function restoreCartLine(root) {
    var nativePrice = root.querySelector('[data-promotion-display-native-price]');
    var promotionalPrice = root.querySelector('[data-promotion-display-cart-price]');
    var totalLabel = root.querySelector('[data-promotion-display-cart-total-label]');
    var label = root.querySelector('[data-promotion-display-label]');

    if (nativePrice) nativePrice.hidden = false;
    if (promotionalPrice) {
      empty(promotionalPrice);
      promotionalPrice.hidden = true;
      promotionalPrice.removeAttribute('aria-label');
    }
    if (totalLabel) totalLabel.hidden = true;
    if (label) {
      label.textContent = '';
      label.hidden = true;
    }
    root.removeAttribute('data-promotion-confirmed');
  }

  function renderCartLine(root, item) {
    var nativePrice = root.querySelector('[data-promotion-display-native-price]');
    var promotionalPrice = root.querySelector('[data-promotion-display-cart-price]');
    var totalLabel = root.querySelector('[data-promotion-display-cart-total-label]');
    var label = root.querySelector('[data-promotion-display-label]');
    if (!nativePrice || !promotionalPrice || !label) return;

    var original = formatMoney(item.lineOriginalCents);
    var discounted = formatMoney(item.lineDiscountedCents);
    empty(promotionalPrice);

    var originalElement = document.createElement('s');
    originalElement.textContent = original;
    var discountedElement = document.createElement('ins');
    discountedElement.textContent = discounted;
    promotionalPrice.appendChild(originalElement);
    promotionalPrice.appendChild(discountedElement);
    promotionalPrice.setAttribute('aria-label', 'Line total. Regular price ' + original + '. Sale price ' + discounted + '.');

    nativePrice.hidden = true;
    promotionalPrice.hidden = false;
    if (totalLabel) totalLabel.hidden = false;
    label.textContent = config.label;
    label.hidden = false;
    root.setAttribute('data-promotion-confirmed', 'true');
  }

  function applyCartLines() {
    // Cart-line pricing is owned exclusively by Discount Ninja's documented
    // data-la-dn-cart-item-* placeholders. The previous theme-side writer
    // could overwrite DN's result after an AJAX drawer refresh and could not
    // safely account for SC Options properties. Keep this compatibility hook
    // inert so legacy events cannot reveal or rewrite the native price.
    return;
  }

  function processCart(payload) {
    var items = payload && field(payload, ['items']);
    if (!Array.isArray(items)) return clearCartConfirmations();

    state.cartLinesByKey = Object.create(null);
    for (var i = 0; i < items.length; i++) {
      var item = normalizeCartItem(items[i]);
      if (isConfirmedCartItem(item)) state.cartLinesByKey[item.key] = item;
    }
    window.requestAnimationFrame(applyCartLines);
  }

  function clearCartConfirmations() {
    state.cartLinesByKey = Object.create(null);
    window.requestAnimationFrame(applyCartLines);
  }

  function resetPdpForVariant() {
    state.lastProductEvent = null;
    clearPdp();
    window.setTimeout(function () {
      observePdp();
      schedulePdp(40);
    }, 0);
  }

  ['la:dn:product:discount:calculated', 'limoniapps:discountninja:product:discountapplied'].forEach(function (eventName) {
    document.addEventListener(eventName, function (event) {
      var record = normalizeProductEvent(getEventData(event));
      var root = productRoot();
      if (!productEventTargetsRoot(record, root)) return;

      state.lastProductEvent = record;
      observePdp();
      schedulePdp(0);
    });
  });

  ['la:dn:cart:updated', 'limoniapps:discountninja:cart:updated'].forEach(function (eventName) {
    document.addEventListener(eventName, function (event) {
      processCart(getEventData(event));
    });
  });

  document.addEventListener('la:dn:cart-mutations:processing', clearCartConfirmations);

  document.addEventListener('theme:cart:added', function () {
    window.requestAnimationFrame(applyCartLines);
  });

  document.addEventListener('theme:product-info:loaded', resetPdpForVariant);
  document.addEventListener('milano:variant-selects-replaced', resetPdpForVariant);
  document.addEventListener('milano:length-media-filtered', resetPdpForVariant);
  document.addEventListener('subscription:planChanged', function (event) {
    var detail = event && event.detail;
    state.selectedSellingPlanId = String(detail && detail.sellingPlanId || '').trim();
    resetPdpForVariant();
  });
  document.addEventListener('change', function (event) {
    if (!event.target || !event.target.closest) return;

    if (event.target.matches && event.target.matches('[name="selling_plan"], .selected-selling-plan-id')) {
      state.selectedSellingPlanId = String(event.target.value || '').trim();
      resetPdpForVariant();
      return;
    }

    if (event.target.closest('.bold_option')) schedulePdp(0);
  }, true);

  if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
    try {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, resetPdpForVariant);
      window.subscribe(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, resetPdpForVariant);
    } catch (error) {
      // Palo Alto's event bus is optional outside a fully initialized PDP.
    }
  }

  window.addEventListener('pageshow', function () {
    observePdp();
    schedulePdp(0);
    window.requestAnimationFrame(applyCartLines);
  });

  enhancePromotionNavigation();
  observePdp();
  schedulePdp(0);
  window.requestAnimationFrame(applyCartLines);
})();
